import { __assign, __extends, __makeTemplateObject, __rest } from "tslib";
import * as React from 'react';
import * as PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Options } from './components/Options';
import { Suggestion } from './Suggestion';
import { mod } from '../utils/helpers';
import { getValueOf, getLabelOf } from '../Input/util';
// Value used to calculate the Options lists max-height.
var OPTIONS_HEIGHT = 35;
var SuggestionFieldComponent = /** @class */ (function (_super) {
    __extends(SuggestionFieldComponent, _super);
    function SuggestionFieldComponent(props) {
        var _this = _super.call(this, props) || this;
        _this.suggestionsRef = React.createRef();
        _this.getChildren = function (shouldRecalcChildren) {
            if (!_this.children || shouldRecalcChildren) {
                _this.children = React.Children.toArray(_this.props.children);
            }
            return _this.children;
        };
        _this.getValueAt = function (index) { return getValueOf(_this.getChildren()[index]); };
        _this.getLabelAt = function (index) { return getLabelOf(_this.getChildren()[index]); };
        _this.getIndexOf = function (value) {
            return _this.getChildren().findIndex(function (i) { return getValueOf(i) === value; });
        };
        _this.getIndexOfSelected = function () { return _this.getIndexOf(_this.props.value); };
        _this.getFocusedSuggestionIndex = function () {
            if (!_this.props.shouldFocusFirstEntry ||
                (_this.state.focusedSuggestionIndex >= 0 &&
                    _this.state.focusedSuggestionIndex < _this.children.length)) {
                return _this.state.focusedSuggestionIndex;
            }
            return _this.children.findIndex(function (child) { return !child.props.isDisabled; });
        };
        _this.scrollTo = function (index) {
            if (_this.suggestionsRef && _this.suggestionsRef.current) {
                var current = _this.suggestionsRef.current;
                var optionElement = current.children[index];
                if (optionElement) {
                    current.scrollTop =
                        index * optionElement.clientHeight - current.clientHeight / 2 + 20;
                }
            }
        };
        _this.handleOptionSelect = function (index, event) {
            var onChange = _this.props.onChange;
            var selectedValue = _this.getValueAt(index);
            var displayValue = _this.getLabelAt(index);
            onChange(event, selectedValue, displayValue);
        };
        _this.handleOptionFocus = function (focusedSuggestionIndex, e) {
            _this.setState({ focusedSuggestionIndex: focusedSuggestionIndex });
            if (e) {
                _this.scrollTo(focusedSuggestionIndex);
            }
        };
        _this.calculateIndex = function (offset) {
            var focusedSuggestionIndex = _this.getFocusedSuggestionIndex();
            var maxResults = _this.props.maxResults;
            var resultsCount = _this.getChildren().length;
            var index = 0;
            if (focusedSuggestionIndex === -1 && offset === -1) {
                index = resultsCount + offset;
            }
            else {
                var limit = maxResults
                    ? Math.min(maxResults, resultsCount)
                    : resultsCount;
                index = mod(focusedSuggestionIndex + offset, limit);
            }
            return index;
        };
        _this.handleKeyDown = function (e) {
            var key = e.key;
            var focusedSuggestionIndex = _this.getFocusedSuggestionIndex();
            var ignoreKey = e.ctrlKey ||
                e.metaKey ||
                e.altKey ||
                key === 'ArrowLeft' ||
                key === 'ArrowRight' ||
                key === 'Shift';
            if (ignoreKey)
                return;
            switch (key) {
                case 'Enter': {
                    var focusedElement = _this.children[focusedSuggestionIndex];
                    if (focusedElement) {
                        if (focusedElement.props.isDisabled) {
                            return;
                        }
                        _this.handleOptionSelect(focusedSuggestionIndex, e);
                    }
                    break;
                }
                case 'ArrowUp':
                    e.preventDefault();
                    if (_this.children.length) {
                        _this.handleOptionFocus(_this.calculateIndex(-1), e);
                    }
                    break;
                case 'ArrowDown':
                    e.preventDefault();
                    if (_this.children.length) {
                        _this.handleOptionFocus(_this.calculateIndex(1), e);
                    }
                    break;
                default:
            }
        };
        _this.unfocus = function () {
            return _this.setState({
                focusedSuggestionIndex: -1,
            });
        };
        _this.children = _this.getChildren();
        _this.state = {
            focusedSuggestionIndex: _this.getIndexOfSelected(),
        };
        return _this;
    }
    SuggestionFieldComponent.prototype.componentDidMount = function () {
        var focusedSuggestionIndex = this.state.focusedSuggestionIndex;
        this.scrollTo(focusedSuggestionIndex);
    };
    SuggestionFieldComponent.prototype.componentDidUpdate = function (prevProps) {
        var value = this.props.value;
        if (prevProps.value !== value) {
            this.unfocus();
        }
    };
    SuggestionFieldComponent.prototype.render = function () {
        var _this = this;
        var _a = this.props, value = _a.value, maxVisibleOptions = _a.maxVisibleOptions, _b = _a.noOptionFound, noOptionFound = _b === void 0 ? 'No option found' : _b, className = _a.className, zIndex = _a.zIndex, onChange = _a.onChange, maxResults = _a.maxResults, children = _a.children, htmlAttributes = __rest(_a, ["value", "maxVisibleOptions", "noOptionFound", "className", "zIndex", "onChange", "maxResults", "children"]);
        var clonedChildren = this.getChildren(true)
            .map(function (child, index) {
            return React.cloneElement(child, {
                isFocused: index === _this.getFocusedSuggestionIndex(),
                isSelected: value === getValueOf(child),
                onMouseMove: function () { return _this.handleOptionFocus(index); },
                onSelect: function (e) {
                    return _this.handleOptionSelect(index, e);
                },
            });
        })
            .slice(0, maxResults);
        return (React.createElement(Options, __assign({ className: className, ref: this.suggestionsRef, style: {
                zIndex: zIndex,
                maxHeight: maxVisibleOptions
                    ? maxVisibleOptions * OPTIONS_HEIGHT
                    : 'none',
            }, 
            // the following line must be overrideable by htmlAttributes:
            onKeyDown: this.handleKeyDown }, htmlAttributes), clonedChildren.length === 0 ? (React.createElement(Suggestion, { isDisabled: true }, noOptionFound)) : (clonedChildren)));
    };
    return SuggestionFieldComponent;
}(React.Component));
export { SuggestionFieldComponent };
export var SuggestionField = styled(SuggestionFieldComponent)(templateObject_1 || (templateObject_1 = __makeTemplateObject([""], [""])));
SuggestionField.propTypes = {
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    maxVisibleOptions: PropTypes.number,
    noOptionFound: PropTypes.string,
    maxResults: PropTypes.number,
    shouldFocusFirstEntry: PropTypes.bool,
    zIndex: PropTypes.number,
    className: PropTypes.string,
    children: PropTypes.node,
};
var templateObject_1;
//# sourceMappingURL=SuggestionField.js.map